/**
 * SSE Session-Events connection-status store (Zustand).
 *
 * Surfaces whether real-time session updates are flowing so the UI can mark
 * data as potentially stale when they're not. The actual SSE connection is
 * owned by `useSessionEvents`; this store is its single output channel.
 *
 * States:
 *  - 'connecting' — initial; before the first response or while reopening.
 *  - 'connected'  — stream is open and bytes are flowing.
 *  - 'disconnected' — established connection dropped and we're between
 *                     reconnect attempts. Live updates are temporarily off
 *                     but list queries will reconcile via refetchOnReconnect.
 *  - 'disabled'   — endpoint returned 503/404 on first attempt; we will not
 *                   retry. The session list still works without SSE.
 *
 * Consumers (HealthStatusBanner, ConversationCard pulse) should treat
 * 'connected' as silent and any other state as a soft hint, never blocking.
 */

import { create } from 'zustand';

export type SSEConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'disabled';

interface SSEConnectionState {
  status: SSEConnectionStatus;
  setStatus: (status: SSEConnectionStatus) => void;
}

export const useSSEConnectionStore = create<SSEConnectionState>((set) => ({
  status: 'connecting',
  setStatus: (status) => set({ status }),
}));

/** Convenience selector hook. */
export function useSSEConnectionStatus(): SSEConnectionStatus {
  return useSSEConnectionStore((s) => s.status);
}
